import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Globe, User, LogOut, Settings, ChevronDown, BookOpen, Layers, Award, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { normalizeSearchQuery } from '../utils/searchNormalize';
import { optimizedCover, isTemporaryCoverUrl } from '../utils/imageUtils';
import { cachedGet } from '../utils/apiCache';
import { bookPath } from '../utils/bookUrl';

const BACKEND_URL = API_BASE_URL;

const remoteCoverFallback = (book, e) => {
  const remote = book?.cover_url_remote;
  if (remote && !isTemporaryCoverUrl(remote) && !e.target.dataset.fb) {
    e.target.dataset.fb = '1';
    e.target.src = remote;
    return;
  }
  e.target.style.display = 'none';
};

// Mega Menu Item Component - Rollink Style with optimized image sizes (max 280px for books)
const MegaMenuItem = ({ item, type, onClose }) => {
  const hasImage = item.image_url && !item.image_url.includes('undefined') && item.image_url.trim() !== '';
  
  // Different icon for each type
  const getDefaultIcon = () => {
    switch(type) {
      case 'books': return <BookOpen className="w-6 h-6 text-gray-300" />;
      case 'series': return <Layers className="w-5 h-5 text-gray-300" />;
      case 'brands': return <Award className="w-5 h-5 text-gray-300" />;
      case 'authors': return <Users className="w-5 h-5 text-gray-300" />;
      default: return <BookOpen className="w-5 h-5 text-gray-300" />;
    }
  };

  // Background colors for placeholder
  const getBgColor = () => {
    switch(type) {
      case 'books': return 'bg-gradient-to-br from-amber-50 to-orange-100';
      case 'series': return 'bg-gradient-to-br from-emerald-50 to-teal-100';
      case 'brands': return 'bg-gradient-to-br from-blue-50 to-indigo-100';
      case 'authors': return 'bg-gradient-to-br from-purple-50 to-pink-100';
      default: return 'bg-gradient-to-br from-gray-50 to-gray-100';
    }
  };

  // Container styles based on type - USER REQUESTED: max 280px for books, proportional for others
  const getContainerStyle = () => {
    switch(type) {
      case 'books': return 'w-[120px] h-[180px]'; // Book cover ratio ~2:3, height ~64% of 280px
      case 'series': return 'w-[100px] h-[100px]'; // Square for series (~36% of 280px)
      case 'brands': return 'w-[100px] h-[60px]'; // Landscape for logos (~21% of 280px)
      case 'authors': return 'w-[80px] h-[80px] rounded-full mx-auto'; // Round for authors (~28% of 280px)
      default: return 'w-[100px] h-[100px]';
    }
  };

  return (
    <Link 
      to={item.url} 
      onClick={onClose}
      className="group flex flex-col items-center text-center"
    >
      {/* Image Container - Optimized sizes per user request */}
      <div className={`relative overflow-hidden ${getContainerStyle()} ${type !== 'authors' ? 'rounded-lg' : ''} bg-gray-100 mb-2 shadow-sm group-hover:shadow-md transition-all duration-300`}>
        {hasImage ? (
          <img 
            src={item.image_url.startsWith('http') ? item.image_url : `${BACKEND_URL}${item.image_url}`}
            alt={item.name}
            className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${type === 'brands' ? 'object-contain p-1.5 bg-white' : ''}`}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={`absolute inset-0 ${getBgColor()} ${hasImage ? 'hidden' : 'flex'} items-center justify-center`}
        >
          {getDefaultIcon()}
        </div>
      </div>
      
      {/* Title */}
      <h3 className="font-medium text-gray-800 group-hover:text-black transition-colors text-xs leading-tight line-clamp-2 max-w-[120px]">
        {item.name}
      </h3>
      {item.role_type && (
        <span className="text-[10px] text-gray-400 mt-0.5 capitalize">{item.role_type}</span>
      )}
    </Link>
  );
};

// Full Width Mega Menu Dropdown - Compact Rollink Style
const MegaMenuDropdown = ({ isOpen, items, title, icon: Icon, linkTo, linkText, onClose, type }) => {
  if (!isOpen) return null;

  const getButtonColor = () => {
    switch(type) {
      case 'books': return 'bg-amber-600 hover:bg-amber-700';
      case 'series': return 'bg-emerald-600 hover:bg-emerald-700';
      case 'brands': return 'bg-blue-600 hover:bg-blue-700';
      case 'authors': return 'bg-purple-600 hover:bg-purple-700';
      default: return 'bg-gray-600 hover:bg-gray-700';
    }
  };

  const getBorderColor = () => {
    switch(type) {
      case 'books': return '#f59e0b';
      case 'series': return '#10b981';
      case 'brands': return '#3b82f6';
      case 'authors': return '#a855f7';
      default: return '#6b7280';
    }
  };
  
  return (
    <div 
      className="fixed left-0 right-0 top-16 bg-white shadow-xl border-t-2 z-[100]"
      style={{ borderTopColor: getBorderColor() }}
      onMouseLeave={onClose}
    >
      <div className="max-w-5xl mx-auto px-6 py-4">
        {/* Header - Compact */}
        <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-gray-600" />
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            <span className="text-xs text-gray-400">({items.length} öne çıkan)</span>
          </div>
          <Link 
            to={linkTo}
            onClick={onClose}
            className={`px-3 py-1.5 ${getButtonColor()} text-white text-xs font-medium rounded-full transition-colors flex items-center gap-1`}
          >
            {linkText}
            <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
          </Link>
        </div>
        
        {/* Items - Compact horizontal flex layout */}
        {items.length > 0 ? (
          <div className="flex items-start justify-center gap-6 flex-wrap">
            {items.map(item => (
              <MegaMenuItem key={item.id} item={item} type={type} onClose={onClose} />
            ))}
          </div>
        ) : (
          <div className="py-6 text-center">
            <Icon className="w-10 h-10 text-gray-200 mx-auto mb-2" />
            <p className="text-gray-400 text-xs">Henüz öne çıkan içerik eklenmedi</p>
          </div>
        )}
      </div>
    </div>
  );
};

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { navbarConfig } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [headerPages, setHeaderPages] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    site_name: '',
    logo_url: null
  });
  
  // Mega menu state
  const [menuSettings, setMenuSettings] = useState({
    featured_books: [],
    featured_series: [],
    featured_brands: [],
    featured_authors: []
  });
  const [seriesMegaData, setSeriesMegaData] = useState({ top_series: [], top_series_books: {} });
  const [seriesMegaState, setSeriesMegaState] = useState('idle'); // idle | loading | loaded | error
  const [booksMegaState, setBooksMegaState] = useState('idle');
  const [brandsMegaState, setBrandsMegaState] = useState('idle');
  const [booksMegaData, setBooksMegaData] = useState({ newest_books: [], most_viewed_books: [] });
  const [brandsMegaData, setBrandsMegaData] = useState({ brands: [] });
  const [authorsMegaData, setAuthorsMegaData] = useState({ featured_authors: [], top_authors: [] });
  const [activeMenu, setActiveMenu] = useState(null);
  const menuTimeoutRef = useRef(null);
  const seriesFetchedRef = useRef(false);
  const booksFetchedRef = useRef(false);
  const brandsFetchedRef = useRef(false);
  const authorsFetchedRef = useRef(false);

  // Hidden login: 5 taps on logo
  const logoTapRef = useRef(0);
  const logoTapTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      try {
        const [settingsRes, pagesRes, menuRes] = await Promise.allSettled([
          cachedGet(`${BACKEND_URL}/api/settings/`),
          cachedGet(`${BACKEND_URL}/api/pages/public?menu_location=header`),
          cachedGet(`${BACKEND_URL}/api/menu-settings/`)
        ]);
        if (cancelled) return;
        if (settingsRes.status === 'fulfilled') setSiteSettings(settingsRes.value.data);
        if (pagesRes.status === 'fulfilled' && pagesRes.value.data.success) setHeaderPages(pagesRes.value.data.pages || []);
        if (menuRes.status === 'fulfilled') setMenuSettings(menuRes.value.data);
      } catch (err) {
        console.error('Navbar fetch error:', err);
      }
    };
    fetchAll();

    // Eagerly pre-fetch ALL mega menu data so the first hover is instant
    seriesFetchedRef.current = true;
    setSeriesMegaState('loading');
    axios.get(`${BACKEND_URL}/api/theme/series-mega-menu`)
      .then(res => { if (!cancelled) { setSeriesMegaData(res.data); setSeriesMegaState('loaded'); } })
      .catch(() => { if (!cancelled) { setSeriesMegaState('error'); seriesFetchedRef.current = false; } });

    booksFetchedRef.current = true;
    setBooksMegaState('loading');
    axios.get(`${BACKEND_URL}/api/theme/books-mega-menu`)
      .then(res => { if (!cancelled) { setBooksMegaData(res.data); setBooksMegaState('loaded'); } })
      .catch(() => { if (!cancelled) { setBooksMegaState('error'); booksFetchedRef.current = false; } });

    brandsFetchedRef.current = true;
    setBrandsMegaState('loading');
    axios.get(`${BACKEND_URL}/api/theme/brands-mega-menu`)
      .then(res => { if (!cancelled) { setBrandsMegaData(res.data); setBrandsMegaState('loaded'); } })
      .catch(() => { if (!cancelled) { setBrandsMegaState('error'); brandsFetchedRef.current = false; } });

    authorsFetchedRef.current = true;
    axios.get(`${BACKEND_URL}/api/theme/authors-mega-menu`)
      .then(res => { if (!cancelled) setAuthorsMegaData(res.data); })
      .catch(() => { if (!cancelled) authorsFetchedRef.current = false; });

    return () => { cancelled = true; };
  }, []);

  const isActive = (path) => location.pathname === path;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/books?q=${encodeURIComponent(normalizeSearchQuery(searchQuery))}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLangMenuOpen(false);
  };

  const handleMenuEnter = (menuName) => {
    if (menuTimeoutRef.current) {
      clearTimeout(menuTimeoutRef.current);
    }
    setActiveMenu(menuName);
    // Lazy-load mega data on first hover
    if (menuName === 'series' && !seriesFetchedRef.current) {
      seriesFetchedRef.current = true;
      setSeriesMegaState('loading');
      axios.get(`${BACKEND_URL}/api/theme/series-mega-menu`)
        .then(res => { setSeriesMegaData(res.data); setSeriesMegaState('loaded'); })
        .catch(() => { setSeriesMegaState('error'); seriesFetchedRef.current = false; });
    }
    if (menuName === 'books' && !booksFetchedRef.current) {
      booksFetchedRef.current = true;
      setBooksMegaState('loading');
      axios.get(`${BACKEND_URL}/api/theme/books-mega-menu`)
        .then(res => { setBooksMegaData(res.data); setBooksMegaState('loaded'); })
        .catch(() => { setBooksMegaState('error'); booksFetchedRef.current = false; });
    }
    if (menuName === 'brands' && !brandsFetchedRef.current) {
      brandsFetchedRef.current = true;
      setBrandsMegaState('loading');
      axios.get(`${BACKEND_URL}/api/theme/brands-mega-menu`)
        .then(res => { setBrandsMegaData(res.data); setBrandsMegaState('loaded'); })
        .catch(() => { setBrandsMegaState('error'); brandsFetchedRef.current = false; });
    }
    if (menuName === 'authors' && !authorsFetchedRef.current) {
      authorsFetchedRef.current = true;
      axios.get(`${BACKEND_URL}/api/theme/authors-mega-menu`)
        .then(res => setAuthorsMegaData(res.data))
        .catch(() => {});
    }
  };

  const handleMenuLeave = () => {
    menuTimeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 150);
  };

  const closeMenu = () => {
    setActiveMenu(null);
  };

  const handleLogoTap = (e) => {
    if (isAuthenticated) return; // Already logged in, no need
    // Only count taps on homepage for secret login
    if (window.location.pathname !== '/') return; // Other pages: let Link navigate normally
    e.preventDefault();
    logoTapRef.current += 1;
    if (logoTapTimerRef.current) clearTimeout(logoTapTimerRef.current);
    logoTapTimerRef.current = setTimeout(() => { logoTapRef.current = 0; }, 2000);
    if (logoTapRef.current >= 5) {
      logoTapRef.current = 0;
      navigate('/login');
    }
  };

  // Dynamic languages from API
  const [dynamicLanguages, setDynamicLanguages] = useState({});
  const langsFetchedRef = useRef(false);

  useEffect(() => {
    if (!langsFetchedRef.current) {
      langsFetchedRef.current = true;
      cachedGet(`${BACKEND_URL}/api/languages/active`)
        .then(res => {
          const langs = res.data.languages || [];
          const langMap = {};
          langs.forEach(l => { langMap[l.code] = `${l.flag || ''} ${l.native_name || l.name}`.trim(); });
          if (Object.keys(langMap).length > 0) setDynamicLanguages(langMap);
        })
        .catch(() => {});
    }
  }, []);

  const languages = Object.keys(dynamicLanguages).length > 0 ? dynamicLanguages : {
    tr: 'Türkçe',
    en: 'English',
    ar: 'العربية'
  };

  // Menu configurations
  const megaMenus = [
    {
      key: 'books',
      label: t('nav.books'),
      path: '/books',
      icon: BookOpen,
      items: menuSettings.featured_books,
      linkText: 'Tüm Kitapları Gör'
    },
    {
      key: 'series',
      label: 'Diziler',
      path: '/series',
      icon: Layers,
      items: menuSettings.featured_series,
      linkText: 'Tüm Dizileri Gör'
    },
    {
      key: 'brands',
      label: 'Markalarımız',
      path: '/brands',
      icon: Award,
      items: menuSettings.featured_brands,
      linkText: 'Tüm Markaları Gör'
    },
    {
      key: 'authors',
      label: t('nav.authors'),
      path: '/authors',
      icon: Users,
      items: menuSettings.featured_authors,
      linkText: 'Tüm Yazarları Gör'
    }
  ];

  const nc = navbarConfig || {};
  const isSticky = nc.sticky !== false;
  const shape = nc.shape || 'full';
  const logoPos = nc.logo_position || 'left';
  const menuPos = nc.menu_position || 'center';
  const navBgColor = nc.bg_color || '';
  const navTextColor = nc.text_color || '';
  const hoverColor = nc.hover_color || '';
  const activeColor = nc.active_color || 'var(--theme-primary)';
  const borderColor = nc.border_color || '';
  const buttonBg = nc.button_bg || 'var(--theme-primary)';
  const buttonText = nc.button_text || '#FFFFFF';

  const resolvedNavBg = navBgColor || 'var(--theme-nav-bg, #1A1A1A)';
  const resolvedNavText = navTextColor || 'var(--theme-nav-text, #FFFFFF)';

  const navStyle = {
    backgroundColor: shape === 'transparent' ? 'transparent' : resolvedNavBg,
    color: resolvedNavText,
    ...(borderColor ? { borderBottom: `2px solid ${borderColor}` } : {})
  };

  const renderLogo = () => (
    <Link to="/" className="flex items-center space-x-2 shrink-0" onClick={(e) => {
      handleLogoTap(e);
      if (window.location.pathname === '/') e.preventDefault();
    }}>
      <div className="flex items-center">
        {siteSettings.logo_url ? (
          <img src={`${BACKEND_URL}${siteSettings.logo_url}`} alt={siteSettings.site_name || 'Logo'} width="120" height="40" fetchpriority="high" decoding="async" className="h-8 sm:h-10 w-auto object-contain" />
        ) : null}
        <span className="ml-2 text-lg sm:text-2xl font-bold tracking-tight whitespace-nowrap" style={{ fontFamily: 'var(--theme-font-heading)' }}>
          {siteSettings.site_name || 'İnsan Yayınları'}
        </span>
      </div>
    </Link>
  );

  const renderMenuLink = (menu) => (
    <div
      key={menu.key}
      className="relative"
      onMouseEnter={() => handleMenuEnter(menu.key)}
      onMouseLeave={handleMenuLeave}
    >
      <Link
        to={menu.path}
        className="px-3 py-2 text-sm font-medium transition-all relative group"
        style={{
          color: isActive(menu.path) ? (activeColor || 'var(--theme-primary)') : resolvedNavText,
          opacity: isActive(menu.path) ? 1 : 0.85,
        }}
        onMouseEnter={(e) => { if (hoverColor) e.target.style.color = hoverColor; }}
        onMouseLeave={(e) => { e.target.style.color = isActive(menu.path) ? (activeColor || 'var(--theme-primary)') : resolvedNavText; }}
      >
        {menu.label}
        {isActive(menu.path) && (
          <span className="absolute -bottom-1 left-0 right-0 h-0.5" style={{ backgroundColor: activeColor || 'var(--theme-primary)' }} />
        )}
      </Link>
    </div>
  );

  const renderMenuLinks = () => (
    <>
      {megaMenus.map(menu => renderMenuLink(menu))}
      {headerPages.map(page => (
        <Link key={page.id} to={`/sayfa/${page.slug}`} className="px-3 py-2 text-sm font-medium transition-opacity"
          style={{ color: isActive(`/sayfa/${page.slug}`) ? activeColor : resolvedNavText, opacity: isActive(`/sayfa/${page.slug}`) ? 1 : 0.85 }}
        >{page.title}</Link>
      ))}
    </>
  );

  const renderDesktopNav = () => (
    <div className="hidden lg:flex items-center space-x-1">
      {renderMenuLinks()}
    </div>
  );

  const renderRightSection = () => (
    <div className="flex items-center space-x-3">
      {/* Search */}
      <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="hover:opacity-75 transition-colors" style={{ color: resolvedNavText }} aria-label="Search">
        <Search size={20} />
      </button>

      {/* Language */}
      <div className="relative hidden lg:block">
        <button onClick={() => setIsLangMenuOpen(!isLangMenuOpen)} className="hover:opacity-75 transition-colors flex items-center space-x-1" style={{ color: resolvedNavText }} aria-label="Change language">
          <Globe size={18} />
          <ChevronDown size={14} />
        </button>
        {isLangMenuOpen && (
          <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border z-50">
            {Object.entries(languages).map(([code, label]) => (
              <button key={code} onClick={() => changeLanguage(code)} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${i18n.language === code ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{label}</button>
            ))}
          </div>
        )}
      </div>

      {/* Auth */}
      {isAuthenticated ? (
        <div className="relative">
          <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="hover:opacity-75 transition-colors flex items-center space-x-2" style={{ color: resolvedNavText }}>
            <User size={18} />
            <span className="hidden lg:inline text-sm">{user?.name || user?.email}</span>
            <ChevronDown size={14} />
          </button>
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border z-50">
              {isAdmin() && (
                <Link to="/admin" onClick={() => setIsUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"><Settings size={14} /> Admin Panel</Link>
              )}
              <button onClick={() => { logout(); setIsUserMenuOpen(false); }} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-50"><LogOut size={14} /> {t('auth.logout')}</button>
            </div>
          )}
        </div>
      ) : null}

      {/* Mobile menu */}
      <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden hover:opacity-75" style={{ color: resolvedNavText }} aria-label="Toggle menu">
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>
    </div>
  );

  return (
    <nav
      className={`${isSticky ? 'sticky top-0' : 'relative'} z-50 ${shape === 'transparent' ? '' : 'shadow-lg'}`}
      style={navStyle}
      data-testid="main-navbar"
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${shape === 'rounded' ? 'py-2' : ''}`}>
        {shape === 'rounded' && (
          <div className="rounded-2xl px-4" style={{ backgroundColor: resolvedNavBg }}>
            <div className={`flex items-center ${logoPos === 'center' ? 'flex-col py-3' : 'justify-between h-16'}`}>
              {renderLogo()}
              {menuPos === 'below' && logoPos === 'center' ? null : renderDesktopNav()}
              {renderRightSection()}
            </div>
            {menuPos === 'below' && (
              <div className="flex items-center justify-center gap-6 pb-2 border-t border-white/10 pt-2">
                {renderMenuLinks()}
              </div>
            )}
          </div>
        )}
        {shape !== 'rounded' && (
          <>
            <div className={`flex items-center ${logoPos === 'center' && menuPos !== 'below' ? 'flex-col py-2' : 'justify-between h-16'}`}>
              {logoPos === 'center' && menuPos !== 'below' ? (
                <>
                  {renderLogo()}
                  <div className="flex items-center gap-6 mt-1">
                    {renderMenuLinks()}
                    {renderRightSection()}
                  </div>
                </>
              ) : (
                <>
                  {renderLogo()}
                  {menuPos !== 'below' && renderDesktopNav()}
                  {renderRightSection()}
                </>
              )}
            </div>
            {menuPos === 'below' && logoPos !== 'center' && (
              <div className="hidden lg:flex items-center justify-center gap-6 pb-2 border-t border-white/10 pt-2">
                {renderMenuLinks()}
              </div>
            )}
          </>
        )}
      </div>

      {/* Mega Menu Dropdowns */}
      {megaMenus.map(menu => (
        <div
          key={`mega-${menu.key}`}
          onMouseEnter={() => handleMenuEnter(menu.key)}
          onMouseLeave={handleMenuLeave}
        >
          {/* === BOOKS MEGA MENU === */}
          {menu.key === 'books' && activeMenu === 'books' ? (
            <div className="fixed left-0 right-0 top-[64px] z-[100] border-t shadow-2xl" style={{ backgroundColor: resolvedNavBg, borderColor: borderColor || 'rgba(255,255,255,0.1)' }}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-12 gap-6">
                  {/* Left: Newest 3 books with covers */}
                  <div className="col-span-4 border-r border-white/10 pr-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                      Yeni Çıkanlar
                    </h3>
                    {booksMegaData.newest_books.length === 0 ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Yükleniyor...</div>
                    ) : (
                      <div className="flex gap-5 items-start">
                        {booksMegaData.newest_books.map(book => (
                          <Link key={book.id} to={bookPath(book)} onClick={closeMenu} className="block group w-[100px]">
                            <div className="w-20 h-[120px] mx-auto rounded shadow-lg overflow-hidden bg-gradient-to-b from-white/10 to-white/5 group-hover:scale-105 transition-transform flex items-center justify-center relative">
                              <img
                                src={optimizedCover(book.cover_url, 200)}
                                alt={book.title}
                                className="w-full h-full object-cover"
                                loading="eager"
                                decoding="async"
                                onError={(e) => remoteCoverFallback(book, e)} />
                              <BookOpen className="w-6 h-6 opacity-30 absolute" style={{ color: resolvedNavText }} />
                            </div>
                            <p className="text-xs font-medium mt-2 text-center leading-tight line-clamp-2 group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>
                              {book.title}
                            </p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right: Most viewed 10 books */}
                  <div className="col-span-8">
                    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                      En Çok Ziyaret Edilen
                    </h3>
                    {booksMegaData.most_viewed_books.length === 0 ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Yükleniyor...</div>
                    ) : (
                    <div className="grid grid-cols-5 gap-x-4 gap-y-4">
                      {booksMegaData.most_viewed_books.slice(0, 10).map(book => (
                        <Link key={book.id} to={bookPath(book)} onClick={closeMenu} className="block group">
                          <div className="w-full aspect-[2/3] max-h-[110px] rounded overflow-hidden bg-gradient-to-b from-white/10 to-white/5 mb-2 relative flex items-center justify-center">
                            {book.cover_url ? (
                              <img
                                src={optimizedCover(book.cover_url, 160)}
                                alt={book.title || ''}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                                loading="eager"
                                decoding="async"
                                onError={(e) => remoteCoverFallback(book, e)}
                              />
                            ) : null}
                            <BookOpen className="w-5 h-5 opacity-30 absolute" style={{ color: resolvedNavText }} />
                          </div>
                          <span className="text-xs font-medium block leading-tight line-clamp-2 group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>
                            {book.title || 'İsimsiz'}
                          </span>
                        </Link>
                      ))}
                    </div>
                    )}
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <Link to="/books" onClick={closeMenu} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: activeColor || 'var(--theme-primary)' }}>
                        Tüm Kitapları Gör →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          /* === SERIES MEGA MENU === */
          ) : menu.key === 'series' && activeMenu === 'series' ? (
            <div className="fixed left-0 right-0 top-[64px] z-[100] border-t shadow-2xl" style={{ backgroundColor: resolvedNavBg, borderColor: borderColor || 'rgba(255,255,255,0.1)' }}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-12 gap-6">
                  {/* Left: Top 3 series - each with 1 book cover */}
                  <div className="col-span-4 border-r border-white/10 pr-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                      Popüler Diziler
                    </h3>
                    {seriesMegaState === 'loading' || (seriesMegaState === 'idle' && seriesMegaData.top_series.length === 0) ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Yükleniyor...</div>
                    ) : seriesMegaState === 'error' ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Yüklenemedi. Tekrar deneyin.</div>
                    ) : seriesMegaData.top_series.length === 0 ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Henüz dizi yok.</div>
                    ) : (
                      <div className="flex gap-5 items-start">
                        {seriesMegaData.top_series.slice(0, 3).map(series => {
                          const books = seriesMegaData.top_series_books[series.id] || [];
                          const firstBook = books[0];
                          return (
                            <div key={series.id} className="flex flex-col items-center text-center w-[100px]">
                              {firstBook ? (
                                <Link to={bookPath(firstBook)} onClick={closeMenu} className="block group mb-2">
                                  <div className="w-20 h-[120px] rounded shadow-lg overflow-hidden bg-gradient-to-b from-white/10 to-white/5 group-hover:scale-105 transition-transform flex items-center justify-center relative">
                                    <img
                                      src={optimizedCover(firstBook.cover_url, 200)}
                                      alt={firstBook.title}
                                      className="w-full h-full object-cover"
                                      loading="eager"
                                      decoding="async"
                                      onError={(e) => remoteCoverFallback(firstBook, e)}
                                    />
                                    <BookOpen className="w-6 h-6 opacity-30 absolute" style={{ color: resolvedNavText }} />
                                  </div>
                                </Link>
                              ) : (
                                <div className="w-20 h-[120px] rounded bg-white/10 flex items-center justify-center mb-2">
                                  <BookOpen className="w-6 h-6 opacity-30" style={{ color: resolvedNavText }} />
                                </div>
                              )}
                              <Link to={`/books?series=${encodeURIComponent(series.name)}`} onClick={closeMenu} className="text-xs font-medium leading-tight hover:opacity-80 transition-opacity line-clamp-2" style={{ color: resolvedNavText }}>
                                {series.name}
                                {series.book_count ? (
                                  <span className="block text-[10px] opacity-60 mt-0.5">{series.book_count} kitap</span>
                                ) : null}
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Right: Top 10 series in 2 rows of 5 */}
                  <div className="col-span-8">
                    <div className="grid grid-cols-5 gap-x-3 gap-y-2">
                      {seriesMegaData.top_series.slice(0, 10).map(series => (
                        <Link key={series.id} to={`/books?series=${encodeURIComponent(series.name)}`} onClick={closeMenu} className="block px-2 py-2 rounded hover:bg-white/5 transition-colors group">
                          <span className="text-sm font-medium block leading-tight group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>{series.name}</span>
                          {series.book_count ? (
                            <span className="text-[10px] opacity-50 block mt-0.5" style={{ color: resolvedNavText }}>{series.book_count} kitap</span>
                          ) : null}
                        </Link>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <Link to="/series" onClick={closeMenu} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: activeColor || 'var(--theme-primary)' }}>
                        Tüm Dizileri Gör →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          /* === BRANDS MEGA MENU === */
          ) : menu.key === 'brands' && activeMenu === 'brands' ? (
            <div className="fixed left-0 right-0 top-[64px] z-[100] border-t shadow-2xl" style={{ backgroundColor: resolvedNavBg, borderColor: borderColor || 'rgba(255,255,255,0.1)' }}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <h3 className="text-xs font-semibold uppercase tracking-wider mb-5" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                  Markalarımız
                </h3>
                {brandsMegaData.brands.length === 0 ? (
                  <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Yükleniyor...</div>
                ) : (
                  <div className="grid grid-cols-4 gap-4">
                    {brandsMegaData.brands.map(brand => (
                      <Link
                        key={brand.id}
                        to={`/books?brand=${encodeURIComponent(brand.name)}`}
                        onClick={closeMenu}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/5 transition-colors group"
                      >
                        {brand.logo_url ? (
                          <img
                            src={`${BACKEND_URL}${brand.logo_url}`}
                            alt={brand.name}
                            className="w-10 h-10 object-contain rounded bg-white/10 p-1 shrink-0"
                            loading="eager"
                            decoding="async"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center shrink-0">
                            <span className="text-sm font-bold opacity-50" style={{ color: resolvedNavText }}>{brand.name.charAt(0)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-sm font-medium block group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>
                            {brand.name}
                          </span>
                          <span className="text-[11px] opacity-60" style={{ color: resolvedNavText }}>
                            {brand.book_count} kitap
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-white/10">
                  <Link to="/brands" onClick={closeMenu} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: activeColor || 'var(--theme-primary)' }}>
                    Tüm Markaları Gör →
                  </Link>
                </div>
              </div>
            </div>

          /* === AUTHORS MEGA MENU === */
          ) : menu.key === 'authors' && activeMenu === 'authors' ? (
            <div className="fixed left-0 right-0 top-[64px] z-[100] border-t shadow-2xl" style={{ backgroundColor: resolvedNavBg, borderColor: borderColor || 'rgba(255,255,255,0.1)' }}>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div className="grid grid-cols-12 gap-6">
                  {/* Left: 3 Featured Authors with photos */}
                  <div className="col-span-4 border-r border-white/10 pr-6">
                    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                      Öne Çıkan Yazarlar
                    </h3>
                    {authorsMegaData.featured_authors.length === 0 ? (
                      <div className="text-xs opacity-50" style={{ color: resolvedNavText }}>Öne çıkan yazar seçilmedi.</div>
                    ) : (
                      <div className="flex gap-5 items-start">
                        {authorsMegaData.featured_authors.map(author => (
                          <Link key={author.id} to={author.url || `/authors/${author.id}`} onClick={closeMenu} className="block group w-[100px]">
                            <div className="w-16 h-16 mx-auto rounded-full overflow-hidden bg-gradient-to-b from-white/10 to-white/5 group-hover:scale-105 transition-transform flex items-center justify-center shadow-lg relative">
                              {author.photo_url ? (
                                <img src={author.photo_url.startsWith('http') ? author.photo_url : `${BACKEND_URL}${author.photo_url}`} alt={author.name} className="w-full h-full object-cover" loading="eager" decoding="async" onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : null}
                              <Users className="w-5 h-5 opacity-30 absolute" style={{ color: resolvedNavText }} />
                            </div>
                            <p className="text-xs font-medium mt-2 text-center leading-tight line-clamp-2 group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>
                              {author.name}
                            </p>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Right: Top 10 Authors by book count */}
                  <div className="col-span-8">
                    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: activeColor || 'var(--theme-primary)', fontFamily: 'var(--theme-font-body)' }}>
                      En Çok Eser Veren Yazarlar
                    </h3>
                    {authorsMegaData.top_authors.length === 0 ? (
                      <div className="text-xs opacity-50 mb-4" style={{ color: resolvedNavText }}>Yazar listesi yüklenemedi veya henüz veri yok.</div>
                    ) : (
                    <div className="grid grid-cols-5 gap-x-3 gap-y-2">
                      {authorsMegaData.top_authors.map(author => (
                        <Link key={author.id} to={`/authors/${encodeURIComponent(author.name)}`} onClick={closeMenu} className="block px-2 py-2 rounded hover:bg-white/5 transition-colors group">
                          <span className="text-sm font-medium block leading-tight group-hover:opacity-80 transition-opacity" style={{ color: resolvedNavText }}>
                            {author.name}
                          </span>
                          {author.book_count ? (
                            <span className="text-[10px] opacity-50 block mt-0.5" style={{ color: resolvedNavText }}>{author.book_count} eser</span>
                          ) : null}
                        </Link>
                      ))}
                    </div>
                    )}
                    <div className="mt-4 pt-3 border-t border-white/10">
                      <Link to="/authors" onClick={closeMenu} className="text-sm font-medium hover:opacity-80 transition-opacity" style={{ color: activeColor || 'var(--theme-primary)' }}>
                        Tüm Yazarları Gör →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          ) : activeMenu === menu.key && menu.items.length > 0 ? (
            <MegaMenuDropdown
              isOpen={true}
              items={menu.items}
              title={menu.label}
              icon={menu.icon}
              linkTo={menu.path}
              linkText={menu.linkText}
              onClose={closeMenu}
              type={menu.key}
            />
          ) : null}
        </div>
      ))}

      {/* Search Bar */}
      {isSearchOpen && (
        <div className="border-t border-white/20" style={{ backgroundColor: resolvedNavBg }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <form onSubmit={handleSearch}>
              <input
                type="text"
                placeholder="Kitap veya yazar ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 rounded-md text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600"
                autoFocus
              />
            </form>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden border-t border-white/20">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {megaMenus.map(menu => (
              <div key={menu.key}>
                <Link
                  to={menu.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10 transition-colors ${
                    isActive(menu.path) ? 'bg-white/20' : ''
                  }`}
                >
                  {menu.label}
                </Link>
                {/* Show featured items in mobile menu */}
                {menu.items.length > 0 && (
                  <div className="ml-4 mt-1 mb-2 space-y-1">
                    {menu.items.slice(0, 3).map(item => (
                      <Link
                        key={item.id}
                        to={item.url}
                        onClick={() => setIsMenuOpen(false)}
                        className="block px-3 py-1.5 text-sm text-gray-300 hover:text-white transition-colors"
                      >
                        • {item.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {/* CMS Header Pages - Mobile, Max 2 */}
            {headerPages.slice(0, 2).map(page => (
              <Link
                key={page.id}
                to={`/sayfa/${page.slug}`}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium hover:bg-white/10 transition-colors ${
                  location.pathname === `/sayfa/${page.slug}` ? 'bg-white/20' : ''
                }`}
              >
                {page.title}
              </Link>
            ))}
            
            {/* Mobile Login/User Menu */}
            {isAuthenticated ? (
              <div className="border-t border-white/20 pt-2 mt-2">
                <div className="px-3 py-2">
                  <div className="flex items-center space-x-3 mb-2">
                    {user?.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name} 
                        className="w-10 h-10 rounded-full border-2 border-white"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                        <User size={20} />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-gray-400">{user?.email}</p>
                    </div>
                  </div>
                  <span className="inline-block px-2 py-1 text-xs font-semibold text-white bg-gray-800 rounded">
                    {user?.role === 'admin' ? 'Admin' : 'Üye'}
                  </span>
                </div>
                {isAdmin() && (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="flex items-center px-3 py-2 rounded-md text-base font-medium hover:bg-white/10 transition-colors"
                  >
                    <Settings size={18} className="mr-2" />
                    Yönetim Paneli
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setIsMenuOpen(false);
                  }}
                  className="flex items-center w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-white/10 transition-colors"
                >
                  <LogOut size={18} className="mr-2" />
                  {t('auth.logout')}
                </button>
              </div>
            ) : null}
            
            <button
              onClick={() => {
                setIsSearchOpen(!isSearchOpen);
                setIsMenuOpen(false);
              }}
              className="w-full text-left px-3 py-2 rounded-md text-base font-medium hover:bg-white/10 transition-colors flex items-center space-x-2"
            >
              <Search size={18} />
              <span>{t('nav.search')}</span>
            </button>

            {/* Mobile Language Selector */}
            <div className="border-t border-white/20 pt-2 mt-2 px-3">
              <div className="flex gap-2">
                {Object.entries(languages).map(([code, label]) => (
                  <button key={code} onClick={() => { changeLanguage(code); setIsMenuOpen(false); }}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${i18n.language === code ? 'bg-white/20 font-bold' : 'hover:bg-white/10 opacity-70'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
